import { EventEmitter } from '../events/EventEmitter';
import type { QueueConfig } from '../types/config';
import { DEFAULT_QUEUE_CONFIG } from '../types/config';
import { CommandTimeoutError } from '../errors/BridgeError';

/**
 * Pending command entry
 */
export interface PendingCommand<T = unknown> {
  /** Unique request ID */
  id: string;

  /** Command action */
  action: string;

  /** Command parameters */
  params?: Record<string, unknown>;

  /** Timeout in milliseconds */
  timeoutMs: number;

  /** Command created timestamp */
  createdAt: Date;

  /** Resolve promise */
  resolve: (value: T) => void;

  /** Reject promise */
  reject: (error: Error) => void;

  /** Timeout handle */
  timeoutId?: ReturnType<typeof setTimeout>;
}

/**
 * Command queue events
 */
export interface CommandQueueEvents {
  /** Command added to queue */
  'command:queued': (command: PendingCommand) => void;

  /** Command started execution */
  'command:started': (command: PendingCommand) => void;

  /** Command completed */
  'command:completed': (id: string, result: unknown) => void;

  /** Command failed */
  'command:failed': (id: string, error: Error) => void;

  /** Command timed out */
  'command:timeout': (id: string) => void;

  /** Queue is empty */
  'queue:empty': () => void;

  /** Queue is full */
  'queue:full': () => void;
}

/**
 * Manages command queuing, deduplication, and concurrency
 */
export class CommandQueue extends EventEmitter<CommandQueueEvents> {
  private readonly config: QueueConfig;
  private readonly pending = new Map<string, PendingCommand>();
  private readonly queue: PendingCommand[] = [];
  private activeCount = 0;
  private requestCounter = 0;
  private readonly requestIdPrefix: string;

  constructor(config?: Partial<QueueConfig>, requestIdPrefix = 'req_') {
    super();
    this.config = { ...DEFAULT_QUEUE_CONFIG, ...config };
    this.requestIdPrefix = requestIdPrefix;
  }

  /**
   * Generate a unique request ID
   */
  generateRequestId(): string {
    return `${this.requestIdPrefix}${Date.now()}_${++this.requestCounter}`;
  }

  /**
   * Add a command to the queue
   *
   * @param action - Command action
   * @param params - Command parameters
   * @param timeoutMs - Optional timeout override
   * @returns Promise that resolves with the command result
   */
  enqueue<T>(
    action: string,
    params?: Record<string, unknown>,
    timeoutMs?: number
  ): { id: string; promise: Promise<T> } {
    const id = this.generateRequestId();
    const timeout = timeoutMs ?? this.config.defaultTimeoutMs;

    // Check capacity
    if (this.config.capacity > 0 && this.queue.length >= this.config.capacity) {
      this.emit('queue:full');
      throw new Error(`Queue is full (capacity: ${this.config.capacity})`);
    }

    // Check for duplicates
    if (this.config.deduplicate) {
      const existing = this.findDuplicate(action, params);
      if (existing) {
        return {
          id: existing.id,
          promise: new Promise((resolve, reject) => {
            const originalResolve = existing.resolve;
            const originalReject = existing.reject;
            existing.resolve = (value) => {
              originalResolve(value);
              resolve(value as T);
            };
            existing.reject = (error) => {
              originalReject(error);
              reject(error);
            };
          }),
        };
      }
    }

    let resolvePromise: (value: T) => void;
    let rejectPromise: (error: Error) => void;

    const promise = new Promise<T>((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });

    const command: PendingCommand<T> = {
      id,
      action,
      params,
      timeoutMs: timeout,
      createdAt: new Date(),
      resolve: resolvePromise!,
      reject: rejectPromise!,
    };

    this.queue.push(command as PendingCommand);
    this.emit('command:queued', command as PendingCommand);

    // Process queue
    this.processQueue();

    return { id, promise };
  }

  /**
   * Start a command (move from queue to pending)
   *
   * @param id - Command ID
   * @returns The command if found and started
   */
  startCommand(id: string): PendingCommand | undefined {
    const index = this.queue.findIndex((cmd) => cmd.id === id);
    if (index === -1) return undefined;

    const command = this.queue[index];
    if (!command) return undefined;

    this.queue.splice(index, 1);

    // Setup timeout
    command.timeoutId = setTimeout(() => {
      this.handleTimeout(id);
    }, command.timeoutMs);

    this.pending.set(id, command);
    this.activeCount++;
    this.emit('command:started', command);

    return command;
  }

  /**
   * Resolve a pending command
   *
   * @param id - Command ID
   * @param result - Command result
   */
  resolve(id: string, result: unknown): boolean {
    const command = this.pending.get(id);
    if (!command) return false;

    this.clearCommand(id);
    command.resolve(result);
    this.emit('command:completed', id, result);
    this.processQueue();

    return true;
  }

  /**
   * Reject a pending command
   *
   * @param id - Command ID
   * @param error - Error that caused rejection
   */
  reject(id: string, error: Error): boolean {
    const command = this.pending.get(id);
    if (!command) return false;

    this.clearCommand(id);
    command.reject(error);
    this.emit('command:failed', id, error);
    this.processQueue();

    return true;
  }

  /**
   * Check if a command is pending
   *
   * @param id - Command ID
   */
  isPending(id: string): boolean {
    return this.pending.has(id);
  }

  /**
   * Get a pending command
   *
   * @param id - Command ID
   */
  getPending(id: string): PendingCommand | undefined {
    return this.pending.get(id);
  }

  /**
   * Get all pending commands
   */
  getAllPending(): PendingCommand[] {
    return Array.from(this.pending.values());
  }

  /**
   * Get queue length
   */
  getQueueLength(): number {
    return this.queue.length;
  }

  /**
   * Get active command count
   */
  getActiveCount(): number {
    return this.activeCount;
  }

  /**
   * Clear all pending commands with an error
   *
   * @param error - Error to reject with
   */
  clearAll(error: Error): void {
    // Clear queue
    for (const command of this.queue) {
      command.reject(error);
    }
    this.queue.length = 0;

    // Clear pending
    for (const [id, command] of this.pending) {
      if (command.timeoutId) {
        clearTimeout(command.timeoutId);
      }
      command.reject(error);
      this.pending.delete(id);
    }

    this.activeCount = 0;
    this.emit('queue:empty');
  }

  /**
   * Process the command queue
   */
  private processQueue(): void {
    while (
      this.queue.length > 0 &&
      this.activeCount < this.config.maxConcurrent
    ) {
      const command = this.queue[0];
      if (!command) break;
      this.startCommand(command.id);
    }

    if (this.queue.length === 0 && this.pending.size === 0) {
      this.emit('queue:empty');
    }
  }

  /**
   * Handle command timeout
   */
  private handleTimeout(id: string): void {
    const command = this.pending.get(id);
    if (!command) return;

    this.clearCommand(id);
    const error = new CommandTimeoutError(command.action, command.timeoutMs);
    command.reject(error);
    this.emit('command:timeout', id);
    this.emit('command:failed', id, error);
    this.processQueue();
  }

  /**
   * Clear a command from pending
   */
  private clearCommand(id: string): void {
    const command = this.pending.get(id);
    if (command) {
      if (command.timeoutId) {
        clearTimeout(command.timeoutId);
      }
      this.pending.delete(id);
      this.activeCount--;
    }
  }

  /**
   * Find a duplicate command in the queue
   */
  private findDuplicate(
    action: string,
    params?: Record<string, unknown>
  ): PendingCommand | undefined {
    const paramsStr = params ? JSON.stringify(params) : '';

    // Check queue
    for (const command of this.queue) {
      if (command.action === action) {
        const cmdParamsStr = command.params ? JSON.stringify(command.params) : '';
        if (cmdParamsStr === paramsStr) {
          return command;
        }
      }
    }

    // Check pending
    for (const command of this.pending.values()) {
      if (command.action === action) {
        const cmdParamsStr = command.params ? JSON.stringify(command.params) : '';
        if (cmdParamsStr === paramsStr) {
          return command;
        }
      }
    }

    return undefined;
  }
}

/**
 * Create a new CommandQueue instance
 */
export function createCommandQueue(
  config?: Partial<QueueConfig>,
  requestIdPrefix?: string
): CommandQueue {
  return new CommandQueue(config, requestIdPrefix);
}
